import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Bell, Check, X } from 'lucide-react';
import { useNotifications } from '@/context/NotificationContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationBell = ({ tokenProp }) => {
  const token = tokenProp || localStorage.getItem('token') || localStorage.getItem('providerToken');
  const { unreadCount, refresh } = useNotifications();
  
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen && token) fetchNotifications();
  }, [isOpen, token]);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.slice(0, 10));
    } catch {}
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      try {
        await axios.put(`${API}/notifications/${notification.id}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        refresh();
        fetchNotifications();
      } catch {}
    }
  };

  const handleDeleteNotification = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      refresh();
      fetchNotifications();
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      refresh();
      fetchNotifications();
    } catch {}
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const diff = Math.floor((Date.now() - date) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell size={20} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-2 border-b">
          <h3 className="font-semibold text-[#1A4D2E]">Notifications</h3>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" onClick={handleMarkAllRead} className="text-xs text-blue-600 hover:text-blue-700">
              <Check size={14} className="mr-1" /> Mark all read
            </Button>
          )}
        </div>
        {notifications.length === 0 ? (
          <div className="px-4 py-8 text-center text-stone-500 text-sm">
            <Bell className="mx-auto mb-2 text-stone-300" size={32} />
            No notifications
          </div>
        ) : (
          <div>
            {notifications.map((notification) => (
              <div key={notification.id} className={`px-4 py-3 border-b ${!notification.read ? 'bg-blue-50' : ''}`}>
                <div className="flex items-start gap-3 w-full">
                  <div
                    className={`mt-1 rounded-full h-2 w-2 flex-shrink-0 cursor-pointer ${!notification.read ? 'bg-blue-600' : 'bg-stone-300'}`}
                    onClick={() => handleNotificationClick(notification)}
                    title={!notification.read ? "Mark as read" : "Read"}
                  />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!notification.read ? 'font-semibold text-[#1A4D2E]' : 'text-stone-700'}`}>
                      {notification.title}
                    </p>
                    <p className="text-xs text-stone-600 mt-1 line-clamp-2">{notification.message}</p>
                    <p className="text-xs text-stone-400 mt-1">{formatTime(notification.created_at)}</p>
                  </div>
                  <Button
                    variant="ghost" size="icon"
                    className="h-6 w-6 flex-shrink-0 text-stone-400 hover:text-red-600"
                    onClick={(e) => handleDeleteNotification(e, notification.id)}
                  >
                    <X size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
